// Ported verbatim from react/src/Components/Opportunity/Elements/OpportunityPerformanceChart.js for the admin tracking-health page; converge to a shared package later.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import commaNumber from 'comma-number';
import moment from 'moment';
import './OpportunityPerformanceChart.scss';

let nextChartId = 0;

const OpportunityPerformanceChart = ({ model, variant = 'legacy', highlightedLineKey = null }) => {
  const containerRef = useRef(null);
  const chartIdRef = useRef(null);
  if (!chartIdRef.current) {
    nextChartId += 1;
    chartIdRef.current = `opc-${nextChartId}`;
  }

  const [size, setSize]         = useState({ w: 0, h: 240 });
  const [hoverIdx, setHoverIdx] = useState(null);
  const tooltipRef = useRef(null);
  const [tooltipH, setTooltipH] = useState(0);

  // Measured after every render so the clamped position tracks row-count changes.
  useLayoutEffect(() => {
    const height = tooltipRef.current?.offsetHeight || 0;
    if (height && height !== tooltipH) setTooltipH(height);
  });
  const {
    dailySeries = [],
    lens,
    todayIdx,
    lines = [],
    tooltipRowsByIndex = [],
    aggregateMode = 'bands',
    frozen = false,
    projectionSource,
    projectionCopy,
    ariaLabel,
    formatXLabel,
    formatTooltipDate
  } = model || {};
  const formatXAxisLabel = date => (formatXLabel ? formatXLabel(date) : formatAxisDate(date, variant));
  const formatHoverDate = formatTooltipDate || getPrettyDate;

  useEffect(() => {
    if (!containerRef.current) return undefined;
    let isActive = true;
    let frameId = null;
    let pendingSize = null;
    let lastSize = { w: 0, h: 240 };
    const ro = new ResizeObserver(entries => {
      if (!isActive) return;
      const entry = entries[entries.length - 1];
      if (!entry) return;
      const nextSize = {
        w: Math.round(entry.contentRect.width),
        h: Math.round(entry.contentRect.height)
      };
      if (
        (pendingSize && pendingSize.w === nextSize.w && pendingSize.h === nextSize.h) ||
        (!pendingSize && lastSize.w === nextSize.w && lastSize.h === nextSize.h)
      ) {
        return;
      }
      pendingSize = nextSize;
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        if (!isActive || !pendingSize) return;
        const next = pendingSize;
        pendingSize = null;
        if (lastSize.w === next.w && lastSize.h === next.h) return;
        lastSize = next;
        setSize(previous => previous.w === next.w && previous.h === next.h ? previous : next);
      });
    });
    ro.observe(containerRef.current);
    return () => {
      isActive = false;
      pendingSize = null;
      ro.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, []);

  const geometry = useMemo(() => {
    if (!size.w || dailySeries.length === 0) return null;
    const hasRightAxis = lines.some(line => line.axis === 'right');
    const padR = hasRightAxis ? 66 : 8, padT = 18, padB = 28;
    const plotH = Math.max(1, size.h - padT - padB);

    const familyMaxes = {};
    lines.forEach(l => {
      const key = `${l.axis}:${l.unitFamily}`;
      const lineMax = Math.max(1, ...l.values.filter(v => v != null));
      familyMaxes[key] = Math.max(familyMaxes[key] || 1, lineMax);
    });

    const series = lines.map(line => {
      const maxY = familyMaxes[`${line.axis}:${line.unitFamily}`] * 1.05;
      return {
        ...line,
        maxY,
        yForVal: v => padT + (1 - v / maxY) * plotH
      };
    });

    const leftLabeler  = series.find(l => l.axis === 'left'  && l.isPrimary)
                      || series.find(l => l.axis === 'left');
    const rightLabeler = series.find(l => l.axis === 'right');

    // Left axis labels sit flush left (aligned with the headline above); the
    // plot starts 24px past the longest tick label.
    const leftFamily = familyOf(leftLabeler);
    const axisFont = variant === 'hero' ? AXIS_LABEL_FONT_HERO : AXIS_LABEL_FONT_LEGACY;
    const maxLabelW = leftLabeler
      ? Math.max(...TICK_FRACTIONS.map(p => measureAxisLabelWidth(formatTick(leftLabeler.maxY * p, leftFamily), axisFont)))
      : 20;
    const padL = Math.ceil(maxLabelW) + 24;
    const plotW = Math.max(1, size.w - padL - padR);
    const xForIdx = i => padL + (i / Math.max(1, dailySeries.length - 1)) * plotW;

    return {
      padL, padR, padT, padB, plotW, plotH,
      hasRightAxis, leftLabeler, rightLabeler,
      xForIdx, series
    };
  }, [size, dailySeries, lines, variant]);

  const handleMove = useCallback(e => {
    if (!geometry || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const localX = (e.clientX - rect.left) - geometry.padL;
    const slot = geometry.plotW / Math.max(1, dailySeries.length - 1);
    const idx = Math.max(0, Math.min(dailySeries.length - 1, Math.round(localX / slot)));
    setHoverIdx(idx);
  }, [geometry, dailySeries.length]);

  const handleLeave = useCallback(() => setHoverIdx(null), []);
  const handleFocus = useCallback(() => {
    if (!dailySeries.length) return;
    setHoverIdx(todayIdx >= 0 ? todayIdx : dailySeries.length - 1);
  }, [dailySeries.length, todayIdx]);
  const handleKeyDown = useCallback(
    event => {
      if (!dailySeries.length) return;
      const current = hoverIdx == null ? (todayIdx >= 0 ? todayIdx : dailySeries.length - 1) : hoverIdx;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setHoverIdx(Math.max(0, current - 1));
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setHoverIdx(Math.min(dailySeries.length - 1, current + 1));
      } else if (event.key === 'Home') {
        event.preventDefault();
        setHoverIdx(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setHoverIdx(dailySeries.length - 1);
      } else if (event.key === 'Escape') {
        setHoverIdx(null);
      }
    },
    [dailySeries.length, hoverIdx, todayIdx]
  );

  if (!geometry || dailySeries.length === 0) {
    return <div className={cn('opportunity-performance-chart', `variant-${variant}`, `lens-${lens}`)} ref={containerRef} />;
  }

  const {
    plotW, plotH, padL, padT, padB,
    hasRightAxis, leftLabeler, rightLabeler,
    xForIdx, series
  } = geometry;
  const baselineY = padT + plotH;
  // Text renders from its baseline, so the visible gap between the plot and
  // the label tops is this offset minus the ~7px cap height.
  const axisLabelY = baselineY + padB - 8;

  const leftFamily  = familyOf(leftLabeler);
  const rightFamily = familyOf(rightLabeler);
  const yTicks = leftLabeler
    ? TICK_FRACTIONS.map(p => ({
        y: padT + (1 - p) * plotH,
        label: formatTick(leftLabeler.maxY * p, leftFamily)
      }))
    : [];
  const yTicksRight = hasRightAxis && rightLabeler
    ? TICK_FRACTIONS.map(p => ({
        y: padT + (1 - p) * plotH,
        label: formatTick(rightLabeler.maxY * p, rightFamily)
      }))
    : [];

  const lastIdx = dailySeries.length - 1;
  const startDate = dailySeries[0]?.date;
  const endDate   = dailySeries[lastIdx]?.date;
  const todayDate = !frozen && todayIdx > 0 && todayIdx < lastIdx ? dailySeries[todayIdx]?.date : null;

  let aggregateBands = null;
  if (lens === 'aggregate' && aggregateMode === 'bands') {
    const gmvSeries        = series.find(s => s.dataKey === 'cumGmv');
    const totalSeries      = series.find(s => s.dataKey === 'cumGmvPlusEmv');
    if (gmvSeries && totalSeries) {
      const lastActualIdx = todayIdx >= 0 ? todayIdx : (dailySeries.length - 1);
      const gmvPoints      = gmvSeries.values.map((v, i)   => ({ x: xForIdx(i), y: gmvSeries.yForVal(v) }));
      const totalPoints    = totalSeries.values.map((v, i) => ({ x: xForIdx(i), y: totalSeries.yForVal(v) }));
      const sliceA = lastActualIdx >= 0 ? lastActualIdx + 1 : 0;
      const gmvActuals     = gmvPoints.slice(0, sliceA);
      const totalActuals   = totalPoints.slice(0, sliceA);

      const gmvBandPath = (() => {
        if (gmvActuals.length < 2) return '';
        const top    = buildSmoothPath(gmvActuals);
        const last   = gmvActuals[gmvActuals.length - 1];
        const first  = gmvActuals[0];
        return `${top} L ${last.x},${baselineY} L ${first.x},${baselineY} Z`;
      })();

      const emvBandPath = (() => {
        if (totalActuals.length < 2 || gmvActuals.length < 2) return '';
        const topPath  = buildSmoothPath(totalActuals);
        const gmvDescPts = [...gmvActuals].reverse();
        const downPath = buildSmoothPath(gmvDescPts).replace(/^M/, 'L');
        return `${topPath} ${downPath} Z`;
      })();

      aggregateBands = { gmvBandPath, emvBandPath };
    }
  }

  const linePaths = series.map(line => {
    const allPoints = line.values
      .map((v, i) => (v == null ? null : { x: xForIdx(i), y: line.yForVal(v), idx: i }))
      .filter(Boolean);

    let actualsPts = [], projPts = [];
    if (allPoints.length > 0) {
      if (todayIdx < 0) {
        projPts = allPoints;
      } else {
        const cutoff = allPoints.findIndex(p => p.idx > todayIdx);
        if (cutoff === -1) {
          actualsPts = allPoints;
        } else if (cutoff === 0) {
          projPts = allPoints;
        } else {
          actualsPts = allPoints.slice(0, cutoff);
          const projectionStartIndex = cutoff - 1;
          projPts = allPoints.slice(projectionStartIndex);
        }
      }
    }

    const actualsD = buildSmoothPath(actualsPts);
    const projD    = projPts.length > 1 ? buildSmoothPath(projPts) : '';
    let areaD = '';
    if (line.hasArea && actualsPts.length > 1) {
      areaD = `${actualsD} L ${actualsPts[actualsPts.length - 1].x},${baselineY} L ${actualsPts[0].x},${baselineY} Z`;
    }
    const pointByIdx = {};
    allPoints.forEach(p => { pointByIdx[p.idx] = p; });
    return { ...line, points: allPoints, pointByIdx, actualsD, projD, areaD };
  });

  const todayX = !frozen && todayIdx >= 0 ? xForIdx(todayIdx) : null;
  // The hero variant rules every labeled tick, top line and baseline included.
  const gridY  = (variant === 'hero' ? [0, 0.25, 0.5, 0.75, 1] : [0.25, 0.5, 0.75]).map(p => padT + plotH * p);

  const lastIdxForBadge   = dailySeries.length - 1;
  const projectionStartX  = !frozen && todayIdx != null && todayIdx >= 0 && todayIdx < lastIdxForBadge ? xForIdx(todayIdx) : null;
  const projectionEndX    = projectionStartX != null ? padL + plotW : null;
  const projectionBadgeX  = projectionStartX != null ? (projectionStartX + projectionEndX) / 2 : null;

  const showHover = hoverIdx != null && hoverIdx >= 0 && hoverIdx < dailySeries.length;
  const hoverX    = showHover ? xForIdx(hoverIdx) : null;
  const hoverRow  = showHover ? dailySeries[hoverIdx] : null;

  const hoverTopY = showHover
    ? Math.min(...linePaths.map(l => l.pointByIdx[hoverIdx]?.y).filter(y => y != null), baselineY)
    : null;
  const tooltipFlipRight = hoverX != null && hoverX < padL + 180;
  const tooltipRows = showHover ? tooltipRowsByIndex[hoverIdx] || [] : [];
  // Clamp vertically inside the chart container so ancestors with
  // overflow: hidden (the hero card) can never clip the tooltip.
  const tooltipDesiredTop = (hoverTopY ?? padT) - 10 - tooltipH;
  const tooltipTop = Math.max(0, Math.min(tooltipDesiredTop, size.h - tooltipH));

  return (
    <div
      className={cn('opportunity-performance-chart', `variant-${variant}`, `lens-${lens}`)}
      ref={containerRef}
      role='group'
      aria-label={ariaLabel}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleLeave}
      onKeyDown={handleKeyDown}
    >
      <svg
        className='chart-svg'
        width={size.w || 0}
        height={size.h || 240}
        aria-hidden='true'
      >
        <defs>
          {linePaths.map(line => (
            <linearGradient
              key={`grad-${line.key}`}
              id={`${chartIdRef.current}-grad-${line.key}`}
              className={`tone-${line.tone}`}
              x1='0' y1='0' x2='0' y2='1'
            >
              {AREA_GRADIENT_STOPS[variant].map(stop => (
                <stop key={stop.offset} offset={stop.offset} stopColor='currentColor' stopOpacity={stop.opacity} />
              ))}
            </linearGradient>
          ))}
          {aggregateBands && (
            <>
              <linearGradient id={`${chartIdRef.current}-grad-agg-gmv`} className='tone-gmv' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%'   stopColor='currentColor' stopOpacity='0.35' />
                <stop offset='100%' stopColor='currentColor' stopOpacity='0' />
              </linearGradient>
              <linearGradient id={`${chartIdRef.current}-grad-agg-emv`} className='tone-emv' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%'   stopColor='currentColor' stopOpacity='0.35' />
                <stop offset='100%' stopColor='currentColor' stopOpacity='0' />
              </linearGradient>
            </>
          )}
        </defs>

        {gridY.map((y, i) => (
          <line
            key={`grid-${i}`}
            className='chart-grid'
            x1={padL} x2={padL + plotW}
            y1={y}    y2={y}
            vectorEffect='non-scaling-stroke'
          />
        ))}

        {yTicks.map(tick => (
          <text
            key={`y-${tick.y}`}
            className='chart-axis-text'
            x={0} y={tick.y + 3}
            textAnchor='start'
          >
            {tick.label}
          </text>
        ))}

        {yTicksRight.map(tick => (
          <text
            key={`yr-${tick.y}`}
            className='chart-axis-text right-axis'
            x={padL + plotW + 24} y={tick.y + 3}
            textAnchor='start'
          >
            {tick.label}
          </text>
        ))}

        {startDate && (
          <text
            className='chart-axis-text x-axis'
            x={padL} y={axisLabelY}
            textAnchor='start'
          >
            {formatXAxisLabel(startDate)}
          </text>
        )}
        {todayDate && (
          <text
            className='chart-axis-text x-axis today'
            x={xForIdx(todayIdx)} y={axisLabelY}
            textAnchor='middle'
          >
            Today
          </text>
        )}
        {endDate && (
          <text
            className='chart-axis-text x-axis'
            x={padL + plotW} y={axisLabelY}
            textAnchor='end'
          >
            {formatXAxisLabel(endDate)}
          </text>
        )}

        {aggregateBands && (
          <>
            {aggregateBands.emvBandPath && (
              <path className='chart-area band' d={aggregateBands.emvBandPath} fill={`url(#${chartIdRef.current}-grad-agg-emv)`} />
            )}
            {aggregateBands.gmvBandPath && (
              <path className='chart-area band' d={aggregateBands.gmvBandPath} fill={`url(#${chartIdRef.current}-grad-agg-gmv)`} />
            )}
          </>
        )}

        {linePaths.map(line => (
          <g
            key={`line-${line.key}-${lens}`}
            className={cn(`tone-${line.tone}`, 'chart-line-group', { dimmed: highlightedLineKey && line.key !== highlightedLineKey })}
          >
            {line.areaD && (
              <path
                className='chart-area'
                d={line.areaD}
                fill={`url(#${chartIdRef.current}-grad-${line.key})`}
              />
            )}
            {line.actualsD && (
              <path
                className={cn('chart-line', { primary: line.isPrimary, secondary: !line.isPrimary, dashed: line.isDashed })}
                d={line.actualsD}
                stroke='currentColor'
                fill='none'
                vectorEffect='non-scaling-stroke'
                // pathLength=1 normalizes the path for the draw animation, but it also makes a pixel
                // dash pattern span the whole line — dashed actuals skip both.
                pathLength={line.isDashed ? undefined : '1'}
                strokeDasharray={line.isDashed ? '5 5' : undefined}
              />
            )}
            {line.projD && (
              <path
                className={cn('chart-line projected', { primary: line.isPrimary, secondary: !line.isPrimary, dashed: line.isDashed })}
                d={line.projD}
                stroke='currentColor'
                fill='none'
                vectorEffect='non-scaling-stroke'
                strokeDasharray={line.isDashed ? '5 5' : '4 4'}
              />
            )}
          </g>
        ))}

        {todayX != null && (
          <line
            className='chart-today-line'
            x1={todayX} x2={todayX}
            y1={padT}   y2={baselineY}
            vectorEffect='non-scaling-stroke'
          />
        )}

        {showHover && hoverX != null && (
          <>
            <line
              className='chart-cursor'
              x1={hoverX} x2={hoverX}
              y1={padT}   y2={baselineY}
              vectorEffect='non-scaling-stroke'
            />
            {linePaths.map(line => {
              const pt = line.pointByIdx[hoverIdx];
              if (!pt) return null;
              return (
                <g key={`dot-${line.key}`} className={`tone-${line.tone}`}>
                  <circle cx={pt.x} cy={pt.y} r={5.5} className='chart-dot-ring' />
                  <circle cx={pt.x} cy={pt.y} r={2.6} className='chart-dot-core' fill='currentColor' />
                </g>
              );
            })}
          </>
        )}

        <rect
          x={padL} y={padT}
          width={plotW} height={plotH}
          fill='transparent'
          onPointerMove={handleMove}
          onPointerDown={handleMove}
          onPointerLeave={handleLeave}
        />
      </svg>

      {showHover && hoverRow && (
        <div
          ref={tooltipRef}
          className={cn('chart-tooltip', { flipped: tooltipFlipRight })}
          style={{ left: hoverX, top: tooltipTop }}
        >
          <div className='tooltip-date'>{formatHoverDate(hoverRow.date)}</div>
          {tooltipRows.map(row => <TooltipRow key={row.key} {...row} />)}
        </div>
      )}

      {projectionBadgeX != null && projectionCopy && (
        <div
          className={cn('projection-label', {
            'plan-baseline': projectionSource === 'plan'
          })}
          style={{ left: projectionBadgeX, top: padT - 2 }}
        >
          <span className='projection-label-text'>{projectionCopy.label}</span>
        </div>
      )}
      <div className='chart-sr-status' aria-live='polite'>
        {showHover && hoverRow
          ? `${formatHoverDate(hoverRow.date)}. ${tooltipRows.map(row => `${row.label}: ${row.value}`).join('. ')}`
          : ''}
      </div>
    </div>
  );
};

OpportunityPerformanceChart.propTypes = {
  model: PropTypes.shape({
    dailySeries: PropTypes.array.isRequired,
    lens: PropTypes.oneOf(['sales', 'media', 'aggregate', 'tracking']).isRequired,
    activeMetric: PropTypes.string,
    todayIdx: PropTypes.number,
    lines: PropTypes.array.isRequired,
    tooltipRowsByIndex: PropTypes.array.isRequired,
    aggregateMode: PropTypes.oneOf(['bands', 'split-emv']),
    frozen: PropTypes.bool,
    projectionSource: PropTypes.oneOf(['plan', 'synthetic', 'partial']),
    projectionCopy: PropTypes.object,
    ariaLabel: PropTypes.string,
    formatXLabel: PropTypes.func,
    formatTooltipDate: PropTypes.func
  }).isRequired,
  variant: PropTypes.oneOf(['legacy', 'hero']),
  highlightedLineKey: PropTypes.string
};

const TICK_FRACTIONS = [0, 0.25, 0.5, 0.75, 1];

// The hero fill is a subtle wash of the tone color (6% at the top, fading out
// at the baseline); the legacy variant keeps its stronger fade.
const AREA_GRADIENT_STOPS = {
  legacy: [
    { offset: '0%', opacity: 0.22 },
    { offset: '100%', opacity: 0 }
  ],
  hero: [
    { offset: '20%', opacity: 0.06 },
    { offset: '100%', opacity: 0 }
  ]
};

// The hero variant zero-pads days ("APR 01") per the leaderboard design.
const formatAxisDate = (date, variant) => (variant === 'hero' ? moment(date).format('MMM DD') : getPrettyDate(date));

const formatTick = (v, family) => {
  if (v <= 0) return family === 'dollar' || family === 'ratio-cost' ? '$0' : (family === 'ratio' ? '0×' : '0');
  if (family === 'count')      return commaNumber(Math.round(v));
  if (family === 'ratio')      return `${v.toFixed(v >= 10 ? 0 : 1)}×`;
  if (family === 'ratio-cost') return v < 10 ? `$${v.toFixed(2)}` : getPrettyMoneyShort(Math.round(v));
  return getPrettyMoneyShort(Math.round(v));
};

const ratioCostKeys = new Set(['cpc', 'cpm']);
const familyOf = labeler => {
  if (!labeler) return 'dollar';
  if (labeler.unitFamily === 'ratio' && ratioCostKeys.has(labeler.key)) return 'ratio-cost';
  return labeler.unitFamily;
};

// Mirrors the .chart-axis-text font per variant. Falls back to a character
// estimate when no 2D canvas context is available (e.g. jsdom).
const AXIS_LABEL_FONT_LEGACY = "500 9px 'DM Sans', 'Karla', sans-serif";
const AXIS_LABEL_FONT_HERO = "400 12px 'DM Sans', 'Karla', sans-serif";
let axisLabelMeasureContext;
const measureAxisLabelWidth = (text, font = AXIS_LABEL_FONT_LEGACY) => {
  if (axisLabelMeasureContext === undefined) {
    axisLabelMeasureContext = document.createElement('canvas').getContext('2d');
  }
  if (!axisLabelMeasureContext) return text.length * 5.5;
  axisLabelMeasureContext.font = font;
  return axisLabelMeasureContext.measureText(text).width;
};

const buildSmoothPath = pts => {
  if (!pts || pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x},${pts[0].y}`;
  const n = pts.length;
  const dx = [];
  const m  = [];
  for (let i = 0; i < n - 1; i++) {
    const _dx = pts[i + 1].x - pts[i].x;
    dx.push(_dx);
    m.push(_dx === 0 ? 0 : (pts[i + 1].y - pts[i].y) / _dx);
  }
  const t = new Array(n).fill(0);
  t[0]     = m[0];
  t[n - 1] = m[n - 2];
  for (let i = 1; i < n - 1; i++) {
    t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) {
      t[i]     = 0;
      t[i + 1] = 0;
    } else {
      const a  = t[i]     / m[i];
      const b  = t[i + 1] / m[i];
      const sq = a * a + b * b;
      if (sq > 9) {
        const f  = 3 / Math.sqrt(sq);
        t[i]     = f * a * m[i];
        t[i + 1] = f * b * m[i];
      }
    }
  }
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < n - 1; i++) {
    const _dx = dx[i];
    const c1x = pts[i].x     + _dx / 3;
    const c1y = pts[i].y     + t[i]     * _dx / 3;
    const c2x = pts[i + 1].x - _dx / 3;
    const c2y = pts[i + 1].y - t[i + 1] * _dx / 3;
    d += ` C ${c1x},${c1y} ${c2x},${c2y} ${pts[i + 1].x},${pts[i + 1].y}`;
  }
  return d;
};

const TooltipRow = ({ tone, label, value, modifier, valueClass, isDashed = false, active = false }) => (
  <div className={cn('tooltip-row', modifier, { active })}>
    <span className={cn('tooltip-swatch', tone ? `tone-${tone}` : 'empty', { dashed: isDashed })} />
    <span className='tooltip-label'>{label}</span>
    <span className={cn('tooltip-value', valueClass)}>{value}</span>
  </div>
);

TooltipRow.propTypes = {
  tone:       PropTypes.string,
  label:      PropTypes.string.isRequired,
  value:      PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  modifier:   PropTypes.string,
  valueClass: PropTypes.string,
  isDashed:   PropTypes.bool,
  active:     PropTypes.bool
};

// Copied from react/src/Helpers/formatting.js so the chart is self-contained; these are only the
// fallback formatters (tracking models pass their own formatXLabel/formatTooltipDate).
const getPrettyDate = (date, formatInSameYear = 'MMM Do', formatInDifferentYear = 'MMM Do, YYYY') => {
  const momentDate = moment(date).local();
  const currentDate = moment().local();
  if (momentDate.isSame(currentDate, 'year')) return momentDate.format(formatInSameYear);
  return momentDate.format(formatInDifferentYear);
};

const getPrettyMoneyShort = v => {
  if (v == null || !Number.isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}k`;
  return `$${Math.round(v).toLocaleString()}`;
};

export default OpportunityPerformanceChart;
